import { useRef, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Check, Loader2, MoveLeft } from 'lucide-react';

import { Input } from 'components/ui/input';
import { PhoneInput } from 'components/ui/phone-input';
import { Button } from 'components/ui/button';
import { Label } from 'components/ui/label';
import EmailVerificationDialog from 'components/dialogs/EmailVerificationDialog';
import useCountdownTimer from 'hooks/useCountdownTimer';
import PhoneVerificationDialog from 'components/dialogs/PhoneVerificationDialog';
import useAuthStore from 'store/auth';
import { useResendSignupOTP, useSignUpV2 } from 'services/auth/hook';
import { SignUpFlowData, SignUpFlowType } from 'services/auth/type';
import { PATH_ROUTES } from 'routes/path';
import { cn } from 'lib/utils';

export default function SignUpVerificationPage() {
  const { t } = useTranslation();
  const { state } = useLocation();
  const navigate = useNavigate();
  console.log('🚀 ~ SignUpVerificationPage ~ state:', state);

  const signupEmail = useAuthStore((store) => store.signupEmail);
  const signupPhone = useAuthStore((store) => store.signupPhone);
  const signupUserId = useAuthStore((store) => store.signupUserId);
  const isEmailVerified = useAuthStore((store) => store.isEmailVerified);
  const isPhoneVerified = useAuthStore((store) => store.isPhoneVerified);
  const setSignupEmail = useAuthStore((store) => store.setSignupEmail);
  const setSignupPhone = useAuthStore((store) => store.setSignupPhone);

  const emailRef = useRef<HTMLInputElement>(null);
  const phoneRef = useRef<HTMLInputElement>(null);

  const [email, setEmail] = useState<string>(signupEmail);
  const [phone, setPhone] = useState<string>(signupPhone);
  const [openEmail, setOpenEmail] = useState<boolean>(false);
  const [openPhone, setOpenPhone] = useState<boolean>(false);
  const [isSendingEmailOTP, setIsSendingEmailOTP] = useState<boolean>(false);
  const [isEmailChanged, setIsEmailChanged] = useState<boolean>(false);
  const [isSendingPhoneOTP, setIsSendingPhoneOTP] = useState<boolean>(false);
  const [isPhoneChanged, setIsPhoneChanged] = useState<boolean>(false);

  const { mutateAsync } = useSignUpV2();
  const { mutateAsync: mutateAsyncSignupOtp } = useResendSignupOTP();

  const {
    getRemainingTime: getEmailRemainingTime,
    restartTimer: restartEmailTimer,
    isRunning: isEmailRunning,
  } = useCountdownTimer(2, false);
  const {
    getRemainingTime: getPhoneRemainingTime,
    restartTimer: restartPhoneTimer,
    isRunning: isPhoneRunning,
  } = useCountdownTimer(2, false);

  const sendEmailOTP = async () => {
    setIsSendingEmailOTP(true);
    let data: SignUpFlowData = { username: email };
    if (isEmailChanged) {
      data = {
        username: signupEmail,
        update_flag: true,
        updated_value: email,
      };
    }
    try {
      if (state?.isOTPSignup) {
        mutateAsyncSignupOtp({
          ...data,
          type: 'login',
        });
      } else {
        await mutateAsync({
          flowType: SignUpFlowType.SEND_EMAIL_OTP,
          data,
        });
      }
      restartEmailTimer();
      setSignupEmail(email);
    } catch (error) {
      throw new Error('Failed to send OTP');
    } finally {
      setIsSendingEmailOTP(false);
    }
  };

  const sendPhoneOTP = async () => {
    setIsSendingPhoneOTP(true);
    let data: SignUpFlowData = { username: signupEmail, user_id: signupUserId };
    if (isPhoneChanged) {
      data = {
        user_id: signupUserId,
        username: signupEmail,
        updated_value: phone,
        update_flag: true,
      };
    }
    try {
      await mutateAsync({
        flowType: SignUpFlowType.SEND_PHONE_OTP,
        data,
      });
      restartPhoneTimer();
      setSignupPhone(phone);
    } catch (error) {
      throw new Error('Failed to send OTP');
    } finally {
      setIsSendingPhoneOTP(false);
    }
  };

  const onVerifyEmail = async () => {
    if (!isEmailRunning || isEmailChanged) {
      try {
        await sendEmailOTP();
        setIsEmailChanged(false);
      } catch (error) {
        return;
      }
    }

    setOpenEmail(true);
  };

  const onVerifyPhone = async () => {
    if (!isPhoneRunning || isPhoneChanged) {
      try {
        await sendPhoneOTP();
        setIsPhoneChanged(false);
      } catch (error) {
        return;
      }
    }

    setOpenPhone(true);
  };

  if (!signupEmail || !signupPhone || !signupUserId) {
    return <Navigate to={PATH_ROUTES.SIGNUP} replace />;
  }

  const onEmailFocus = () => {
    setTimeout(() => {
      emailRef?.current?.focus();
    }, 500);
  };

  const onPhoneFocus = () => {
    setTimeout(() => {
      phoneRef?.current?.focus();
    }, 500);
  };

  const onEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setEmail(value);
    setIsEmailChanged(value === signupEmail ? false : true);
  };

  const onPhoneChange = (num: string) => {
    setPhone(num);
    setIsPhoneChanged(true);
    setIsPhoneChanged(num === signupPhone ? false : true);
  };

  return (
    <>
      <div className='absolute hidden lg:block top-4 left-4'>
        <Button
          variant='ghost'
          className='rounded-md'
          onClick={() => navigate(-1)}
        >
          <MoveLeft className='w-5 h-5' />
        </Button>
      </div>

      <div className='flex flex-col items-center w-full space-y-8'>
        <div className='w-4/5 mx-auto sm:w-2/5'>
          <h2 className='text-2xl font-semibold leading-none tracking-tight text-center transition-colors text-primary'>
            {t('complete_signup_verification')}
          </h2>
        </div>

        <div className='w-full px-8 space-y-8 sm:w-2/3'>
          <div>
            <div>
              <Label htmlFor='email'>{t('email')}</Label>
              <div className='relative'>
                <Input
                  ref={emailRef}
                  id='email'
                  value={email}
                  onChange={onEmailChange}
                  disabled={isEmailVerified}
                  className='pr-20'
                />
                <div
                  className={cn(
                    'absolute top-1 right-0',
                    isEmailVerified && 'top-2 right-2'
                  )}
                >
                  {isEmailVerified ? (
                    <div className='grid rounded-full size-5 place-content-center bg-primary/70'>
                      <span className='sr-only'>Email verified</span>
                      <Check className='text-white size-3' />
                    </div>
                  ) : (
                    <Button
                      disabled={!email || isSendingEmailOTP}
                      onClick={onVerifyEmail}
                      className='px-3 text-sm h-7'
                    >
                      {isSendingEmailOTP && (
                        <Loader2
                          role='progressbar'
                          className='mr-1 animate-spin size-4'
                        />
                      )}
                      <span className='sr-only'>Verify email</span>
                      {t('verify')}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div>
            <div>
              <Label htmlFor='phone'>{t('phone')}</Label>
              <div className='relative'>
                <PhoneInput
                  // @ts-expect-error correctly passed ref
                  ref={phoneRef}
                  id='phone'
                  defaultCountry='MX'
                  value={phone}
                  onChange={onPhoneChange}
                  disabled={isPhoneVerified}
                />
                <div
                  className={cn(
                    'absolute top-1 right-0',
                    isPhoneVerified && 'top-2 right-2'
                  )}
                >
                  {isPhoneVerified ? (
                    <div className='grid rounded-full size-5 place-content-center bg-primary/70'>
                      <span className='sr-only'>Phone verified</span>
                      <Check className='text-white size-3' />
                    </div>
                  ) : (
                    <Button
                      disabled={!phone || isSendingPhoneOTP}
                      onClick={onVerifyPhone}
                      className='px-3 text-sm h-7'
                    >
                      {isSendingPhoneOTP && (
                        <Loader2
                          role='progressbar'
                          className='mr-1 animate-spin size-4'
                        />
                      )}
                      <span className='sr-only'>Verify phone</span>
                      {t('verify')}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {isPhoneVerified && isEmailVerified ? (
          <div className='!mt-12 text-center'>
            <p className='text-lg font-medium leading-tight text-primary'>
              {t('thank_you')} <br /> {t('verification_completion_msg')}
            </p>
          </div>
        ) : (
          <div className='max-w-sm'>
            <div className='!mt-12 text-center space-y-2'>
              {isPhoneVerified && (
                <p className='font-medium leading-tight text-primary'>
                  {t('phone_verification_thank_msg')}
                </p>
              )}
              {isEmailVerified ? (
                <>
                  <p className='font-medium leading-tight text-primary'>
                    {t('email_verification_thank_msg')}
                  </p>
                  {!isPhoneVerified && (
                    <p className='font-medium leading-tight text-primary'>
                      {t('verify_phone_msg')}
                    </p>
                  )}
                </>
              ) : (
                <p className='font-medium leading-tight text-primary'>
                  {t('verify_email_msg')}
                </p>
              )}
            </div>
          </div>
        )}
      </div>

      <EmailVerificationDialog
        email={email}
        open={openEmail}
        onClose={() => setOpenEmail(false)}
        getRemainingTime={getEmailRemainingTime}
        onResend={sendEmailOTP}
        isSending={isSendingEmailOTP}
        onEmailFocus={onEmailFocus}
      />

      <PhoneVerificationDialog
        phone={phone}
        open={openPhone}
        onClose={() => setOpenPhone(false)}
        getRemainingTime={getPhoneRemainingTime}
        onResend={sendPhoneOTP}
        isSending={isSendingPhoneOTP}
        onPhoneFocus={onPhoneFocus}
      />
    </>
  );
}
